import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Node } from '@tiptap/core';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  ArrowDown,
  ArrowUp,
  Bold,
  FileUp,
  Table2,
  Video,
  Heading2,
  Heading3,
  ImagePlus,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Minus,
  Pilcrow,
  Quote,
  Redo2,
  Undo2,
  Unlink,
} from 'lucide-react';
import { toast } from 'sonner';
import { parseVideoUrl, safeHref } from '@zt/content';
import { Button, Dialog, Field, Input, Select, Textarea } from '@zt/ui';
import { api, ApiError } from '../lib/api';
import { canMoveBlock, moveBlock } from '../lib/editor-move';
import { HelpTip } from '../lib/help';
import { useT } from '../lib/i18n';

/**
 * Zengin metin editörü (TipTap). Yalnızca vitrinin çizebildiği öğeler vardır: paragraf, başlık (2–4),
 * listeler, alıntı, ayırıcı, kalın, italik, bağlantı, yüklenen görsel (alt metin zorunlu), tablo (hücrede
 * paragraf, birleştirme yok) ve YouTube/Vimeo videosu (F5B-18). Yapıştırılan HTML bu öğelere indirgenir;
 * Markdown/HTML "İçe al" ile güvenli dönüştürülür. Çıktı JSON'dur ve sunucuda yeniden doğrulanır (packages/content).
 */

/** Tablo hücreleri yalnızca paragraf içerir (vitrin ve doğrulama ile aynı). */
const ContentTableCell = TableCell.extend({ content: 'paragraph+' });
const ContentTableHeader = TableHeader.extend({ content: 'paragraph+' });

/** Video: sağlayıcı + kimlik + başlık. Editörde yer tutucu kutu olarak görünür; panel dış hizmete istek göndermez. */
const ContentVideo = Node.create({
  name: 'video',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    return { provider: { default: null }, videoId: { default: null }, title: { default: '' } };
  },
  parseHTML() {
    return [];
  },
  renderHTML({ node }) {
    const { provider, title } = node.attrs as { provider: string; title: string };
    return [
      'div',
      {
        class: 'my-2 rounded-sm border border-line bg-surface-2 px-3 py-4 text-sm text-ink-2',
        'data-video': provider,
      },
      `▶ ${provider === 'vimeo' ? 'Vimeo' : 'YouTube'} · ${title}`,
    ];
  },
});

/** Yüklenen görsel: dosya kimliği + alt metin + isteğe bağlı altyazı. Yapıştırılan dış görseller kabul edilmez. */
const ContentImage = Node.create({
  name: 'image',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    return { fileId: { default: null }, alt: { default: '' }, caption: { default: null } };
  },
  parseHTML() {
    return [];
  },
  renderHTML({ node }) {
    const { fileId, alt, caption } = node.attrs as { fileId: string; alt: string; caption: string | null };
    const img = ['img', { src: `/v1/files/${fileId}/content`, alt, class: 'max-h-80 rounded-sm' }] as const;
    return caption
      ? ['figure', {}, img, ['figcaption', { class: 'text-sm text-ink-3' }, caption]]
      : ['figure', {}, img];
  },
});

export function RichTextEditor({
  value,
  onChange,
  label,
  id,
  help,
}: {
  value: unknown;
  onChange: (doc: unknown) => void;
  label: string;
  id: string;
  help?: ReactNode;
}) {
  const t = useT();
  const [linkOpen, setLinkOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [imageFile, setImageFile] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3, 4] },
        code: false,
        codeBlock: false,
        strike: false,
        underline: false,
        link: {
          openOnClick: false,
          autolink: false,
          HTMLAttributes: { rel: null, target: null },
          isAllowedUri: (url) => !!safeHref(url),
        },
      }),
      ContentImage,
      Table.configure({ resizable: false, allowTableNodeSelection: false }),
      TableRow,
      ContentTableHeader,
      ContentTableCell,
      ContentVideo,
    ],
    content: (value as object) ?? { type: 'doc', content: [] },
    editorProps: {
      attributes: {
        id,
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': label,
        class: 'zt-prose min-h-40 px-3 py-2 focus:outline-none',
      },
    },
    onUpdate: ({ editor: e }) => onChange(e.getJSON()),
  });
  // Dışarıdan (ör. kayıttan sonra) gelen içerik değişirse editöre yansıt.
  useEffect(() => {
    if (!editor || editor.isFocused) return;
    const incoming = JSON.stringify(value ?? null);
    if (incoming !== JSON.stringify(editor.getJSON()))
      editor.commands.setContent((value as object) ?? '', { emitUpdate: false });
  }, [value, editor]);
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) =>
      e
        ? {
            bold: e.isActive('bold'),
            italic: e.isActive('italic'),
            h2: e.isActive('heading', { level: 2 }),
            h3: e.isActive('heading', { level: 3 }),
            bullet: e.isActive('bulletList'),
            ordered: e.isActive('orderedList'),
            quote: e.isActive('blockquote'),
            link: e.isActive('link'),
            table: e.isActive('table'),
            canUndo: e.can().undo(),
            canRedo: e.can().redo(),
            canUp: canMoveBlock(e.state, -1),
            canDown: canMoveBlock(e.state, 1),
          }
        : null,
  });
  if (!editor || !state) return null;
  const tool = (key: string, icon: ReactNode, action: () => void, active?: boolean, disabled?: boolean) => (
    <button
      key={key}
      type="button"
      title={t(`admin.editor.${key}`)}
      aria-label={t(`admin.editor.${key}`)}
      aria-pressed={active}
      disabled={disabled}
      onClick={action}
      className="rounded-sm p-1.5 text-ink-2 hover:bg-surface-2 hover:text-ink aria-pressed:bg-pine-tint aria-pressed:text-pine disabled:opacity-40"
    >
      {icon}
    </button>
  );
  const c = () => editor.chain().focus();
  const i = 'h-4 w-4';
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <label htmlFor={id} className="text-sm font-medium text-ink">
          {label}
        </label>
        {help}
      </div>
      <div className="rounded-sm border border-line-strong bg-surface focus-within:border-pine focus-within:ring-2 focus-within:ring-pine/25">
        <div
          role="toolbar"
          aria-label={t('admin.editor.toolbar')}
          className="flex flex-wrap gap-0.5 border-b border-line p-1"
        >
          {tool('paragraph', <Pilcrow className={i} aria-hidden />, () => c().setParagraph().run())}
          {tool(
            'heading2',
            <Heading2 className={i} aria-hidden />,
            () => c().toggleHeading({ level: 2 }).run(),
            state.h2,
          )}
          {tool(
            'heading3',
            <Heading3 className={i} aria-hidden />,
            () => c().toggleHeading({ level: 3 }).run(),
            state.h3,
          )}
          {tool('bold', <Bold className={i} aria-hidden />, () => c().toggleBold().run(), state.bold)}
          {tool('italic', <Italic className={i} aria-hidden />, () => c().toggleItalic().run(), state.italic)}
          {tool(
            'bulletList',
            <List className={i} aria-hidden />,
            () => c().toggleBulletList().run(),
            state.bullet,
          )}
          {tool(
            'orderedList',
            <ListOrdered className={i} aria-hidden />,
            () => c().toggleOrderedList().run(),
            state.ordered,
          )}
          {tool(
            'blockquote',
            <Quote className={i} aria-hidden />,
            () => c().toggleBlockquote().run(),
            state.quote,
          )}
          {tool('rule', <Minus className={i} aria-hidden />, () => c().setHorizontalRule().run())}
          {tool('link', <LinkIcon className={i} aria-hidden />, () => setLinkOpen(true), state.link)}
          {tool(
            'unlink',
            <Unlink className={i} aria-hidden />,
            () => c().unsetLink().run(),
            false,
            !state.link,
          )}
          {tool('image', <ImagePlus className={i} aria-hidden />, () => fileInput.current?.click())}
          {tool(
            'table',
            <Table2 className={i} aria-hidden />,
            () => c().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
            state.table,
          )}
          {tool('video', <Video className={i} aria-hidden />, () => setVideoOpen(true))}
          {tool('import', <FileUp className={i} aria-hidden />, () => setImportOpen(true))}
          {/* Sürüklemeye alternatif (WCAG 2.5.7): seçili bloğu (görsel, video, paragraf…) taşı. */}
          {(['moveUp', 'moveDown'] as const).map((k) =>
            tool(
              k,
              k === 'moveUp' ? (
                <ArrowUp className={i} aria-hidden />
              ) : (
                <ArrowDown className={i} aria-hidden />
              ),
              () => {
                const tr = moveBlock(editor.state, k === 'moveUp' ? -1 : 1);
                if (tr) editor.view.dispatch(tr);
                editor.commands.focus();
              },
              false,
              k === 'moveUp' ? !state.canUp : !state.canDown,
            ),
          )}
          {tool('undo', <Undo2 className={i} aria-hidden />, () => c().undo().run(), false, !state.canUndo)}
          {tool('redo', <Redo2 className={i} aria-hidden />, () => c().redo().run(), false, !state.canRedo)}
        </div>
        {state.table && (
          <div
            role="toolbar"
            aria-label={t('admin.editor.tableToolbar')}
            className="flex flex-wrap items-center gap-1 border-b border-line bg-surface-2 px-2 py-1 text-xs"
          >
            {(
              [
                ['addRow', () => c().addRowAfter().run()],
                ['addColumn', () => c().addColumnAfter().run()],
                ['deleteRow', () => c().deleteRow().run()],
                ['deleteColumn', () => c().deleteColumn().run()],
                ['deleteTable', () => c().deleteTable().run()],
              ] as const
            ).map(([k, run]) => (
              <Button key={k} size="sm" variant="ghost" onClick={run}>
                {t(`admin.editor.${k}`)}
              </Button>
            ))}
            <HelpTip k="editor.table" />
          </div>
        )}
        <EditorContent editor={editor} />
      </div>
      <input
        ref={fileInput}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/avif,image/gif"
        tabIndex={-1}
        aria-label={t('admin.editor.image')}
        className="sr-only"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await api.upload<{ id: string }>('/v1/admin/files?purpose=general', fd);
            setImageFile(r.id);
          } catch (err) {
            toast.error(err instanceof ApiError ? err.title : String(err));
          } finally {
            if (fileInput.current) fileInput.current.value = '';
          }
        }}
      />
      <LinkDialog
        open={linkOpen}
        initial={(editor.getAttributes('link').href as string | undefined) ?? ''}
        onClose={() => setLinkOpen(false)}
        onApply={(href, newTab) => {
          c()
            .extendMarkRange('link')
            .setLink({ href, target: newTab ? '_blank' : null })
            .run();
          setLinkOpen(false);
        }}
      />
      <VideoDialog
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        onInsert={(attrs) => {
          c().insertContent({ type: 'video', attrs }).run();
          setVideoOpen(false);
        }}
      />
      {importOpen && (
        <ImportDialog
          onClose={() => setImportOpen(false)}
          onInsert={(content) => {
            c().insertContent(content).run();
            setImportOpen(false);
          }}
        />
      )}
      <ImageAltDialog
        fileId={imageFile}
        onClose={() => setImageFile(null)}
        onInsert={(alt, caption) => {
          c()
            .insertContent({ type: 'image', attrs: { fileId: imageFile, alt, caption: caption || null } })
            .run();
          setImageFile(null);
        }}
      />
    </div>
  );
}

function LinkDialog({
  open,
  initial,
  onClose,
  onApply,
}: {
  open: boolean;
  initial: string;
  onClose: () => void;
  onApply: (href: string, newTab: boolean) => void;
}) {
  const t = useT();
  const [href, setHref] = useState(initial);
  const [newTab, setNewTab] = useState(false);
  const [error, setError] = useState<string | undefined>();
  useEffect(() => {
    if (open) {
      setHref(initial);
      setError(undefined);
    }
  }, [open, initial]);
  const submit = () => {
    const safe = safeHref(href);
    if (!safe) return setError(t('errors.content_invalid_link'));
    onApply(safe, newTab);
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.editor.linkTitle')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit}>
            {t('admin.editor.linkApply')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field
          label={t('admin.editor.linkUrl')}
          help={<HelpTip k="editor.link_url" />}
          error={error}
          description={t('admin.editor.linkUrlLead')}
        >
          {(p) => (
            <Input
              {...p}
              value={href}
              autoFocus
              spellCheck={false}
              onChange={(e) => setHref(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submit();
                }
              }}
            />
          )}
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={newTab}
            onChange={(e) => setNewTab(e.target.checked)}
            className="h-4 w-4"
          />
          {t('admin.editor.linkNewTab')}
        </label>
      </div>
    </Dialog>
  );
}

function ImageAltDialog({
  fileId,
  onClose,
  onInsert,
}: {
  fileId: string | null;
  onClose: () => void;
  onInsert: (alt: string, caption: string) => void;
}) {
  const t = useT();
  const [alt, setAlt] = useState('');
  const [caption, setCaption] = useState('');
  const [error, setError] = useState<string | undefined>();
  useEffect(() => {
    setAlt('');
    setCaption('');
    setError(undefined);
  }, [fileId]);
  return (
    <Dialog
      open={!!fileId}
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.editor.imageTitle')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            onClick={() =>
              alt.trim()
                ? onInsert(alt.trim(), caption.trim())
                : setError(t('errors.content_image_alt_required'))
            }
          >
            {t('admin.editor.imageInsert')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {fileId && (
          <img
            src={`/v1/files/${fileId}/content`}
            alt=""
            className="max-h-48 rounded-sm border border-line"
          />
        )}
        <Field
          label={t('admin.editor.imageAlt')}
          help={<HelpTip k="editor.image_alt" />}
          description={t('admin.editor.imageAltLead')}
          error={error}
          required
        >
          {(p) => (
            <Input {...p} value={alt} maxLength={300} autoFocus onChange={(e) => setAlt(e.target.value)} />
          )}
        </Field>
        <Field
          label={t('admin.editor.imageCaption')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="editor.image_caption" />}
        >
          {(p) => (
            <Input {...p} value={caption} maxLength={500} onChange={(e) => setCaption(e.target.value)} />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

/** Video ekle (F5B-18): YouTube veya Vimeo adresi + başlık (vitrinde oynatıcı çerçevesinin adı). */
function VideoDialog({
  open,
  onClose,
  onInsert,
}: {
  open: boolean;
  onClose: () => void;
  onInsert: (attrs: { provider: string; videoId: string; title: string }) => void;
}) {
  const t = useT();
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [errors, setErrors] = useState<{ url?: string; title?: string }>({});
  useEffect(() => {
    if (open) {
      setUrl('');
      setTitle('');
      setErrors({});
    }
  }, [open]);
  const submit = () => {
    const v = parseVideoUrl(url);
    const e = {
      url: v ? undefined : t('errors.video_url_invalid'),
      title: title.trim() ? undefined : t('errors.content_video_title_required'),
    };
    setErrors(e);
    if (v && !e.title) onInsert({ ...v, title: title.trim() });
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.editor.videoTitle')}
      description={t('admin.editor.videoLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit}>
            {t('admin.editor.videoInsert')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field
          label={t('admin.editor.videoUrl')}
          required
          help={<HelpTip k="editor.video" />}
          error={errors.url}
        >
          {(p) => (
            <Input
              {...p}
              value={url}
              autoFocus
              spellCheck={false}
              placeholder="https://www.youtube.com/watch?v=…"
              onChange={(e) => setUrl(e.target.value)}
            />
          )}
        </Field>
        <Field label={t('admin.editor.videoName')} required error={errors.title}>
          {(p) => <Input {...p} value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />}
        </Field>
      </div>
    </Dialog>
  );
}

/**
 * İçe al (F5B-18): Markdown veya HTML metni güvenli dönüştürülüp imlecin olduğu yere eklenir. Dönüştürücü
 * yalnızca bu pencere açılınca yüklenir. Atılan öğeler (betik, dış görsel, izin dışı gömme) sayıyla bildirilir.
 */
function ImportDialog({
  onClose,
  onInsert,
}: {
  onClose: () => void;
  onInsert: (content: unknown[]) => void;
}) {
  const t = useT();
  const [format, setFormat] = useState<'markdown' | 'html'>('markdown');
  const [text, setText] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const { docFromHtml, docFromMarkdown } = await import('@zt/content/import');
      const r = format === 'html' ? docFromHtml(text) : docFromMarkdown(text);
      if (!r.doc.content.length) {
        setError(t('admin.editor.importEmpty'));
        return;
      }
      const skipped = [
        r.skipped.images && t('admin.editor.importSkippedImages', { count: r.skipped.images }),
        r.skipped.scripts && t('admin.editor.importSkippedScripts', { count: r.skipped.scripts }),
        r.skipped.embeds && t('admin.editor.importSkippedEmbeds', { count: r.skipped.embeds }),
      ].filter(Boolean);
      onInsert(r.doc.content);
      toast.success(
        skipped.length ? `${t('admin.editor.imported')} ${skipped.join(' ')}` : t('admin.editor.imported'),
      );
    } catch (e) {
      const code = (e as { issues?: Array<{ code: string }> }).issues?.[0]?.code;
      setError(code ? t(`errors.${code}`) : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.editor.importTitle')}
      description={t('admin.editor.importLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={busy} disabled={!text.trim()} onClick={() => void run()}>
            {t('admin.editor.importRun')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label={t('admin.editor.importFormat')} help={<HelpTip k="editor.import" />}>
          {(p) => (
            <Select {...p} value={format} onChange={(e) => setFormat(e.target.value as typeof format)}>
              <option value="markdown">Markdown</option>
              <option value="html">HTML</option>
            </Select>
          )}
        </Field>
        <Field label={t('admin.editor.importText')} required error={error}>
          {(p) => (
            <Textarea
              {...p}
              className="min-h-48 font-mono text-sm"
              value={text}
              spellCheck={false}
              onChange={(e) => setText(e.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
